import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { Crown, ArrowRight, ArrowLeft, Eye, EyeSlash } from '@phosphor-icons/react';
import BubbleLogo from '@/components/BubbleLogo';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { track } from '@/lib/track';

const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [form, setForm] = useState({ email: '', username: '', password: '', invite_code: 'GENESIS' });
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { track('signup_started', { source: 'register' }); }, []);
  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    const code = q.get('invite');
    if (code) setForm((f) => ({ ...f, invite_code: code.toUpperCase() }));
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register({
        ...form,
        email: form.email.trim().toLowerCase(),
        username: form.username.trim(),
      });
      toast.success(`Welcome, founder #${u.founder_number}.`);
      nav('/onboarding');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="register-back" className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white mb-4">
          <ArrowLeft size={14} weight="bold" /> Back
        </Link>
        <div className="grad-border p-8">
          <div className="mb-6">
            <BubbleLogo to="/" size="md" />
          </div>
          <div className="chip text-bx-gold border-bx-gold/40 mb-4">
            <Crown size={12} weight="fill" /> Founder access
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter">Claim your invite</h1>
          <p className="text-white/55 mt-1 text-sm">
            Use code <code className="text-bx-cyan font-mono">GENESIS</code> if you don't have a personal invite.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">email</span>
              <input data-testid="register-email" type="email" required autoComplete="email"
                     value={form.email} onChange={set('email')}
                     className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm transition" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">username</span>
              <input data-testid="register-username" type="text" required minLength={3} maxLength={24}
                     value={form.username} onChange={set('username')}
                     placeholder="how the network calls you"
                     className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm transition" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">password</span>
              <div className="mt-1 flex items-center gap-2 px-3 rounded-xl bg-white/5 border border-white/10 focus-within:border-bx-cyan transition">
                <input data-testid="register-password" type={show ? 'text' : 'password'}
                       required minLength={6} autoComplete="new-password"
                       value={form.password} onChange={set('password')}
                       placeholder="at least 6 characters"
                       className="bg-transparent flex-1 py-3 outline-none text-sm" />
                <button type="button" data-testid="register-show-password"
                        onClick={() => setShow((s) => !s)} className="text-white/60 hover:text-white p-1">
                  {show ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                </button>
              </div>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">invite code</span>
              <input data-testid="register-invite_code" type="text"
                     value={form.invite_code} onChange={set('invite_code')}
                     className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm font-mono uppercase transition" />
            </label>
            <button data-testid="register-submit" disabled={busy}
                    className="w-full btn-primary py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? 'Reserving your spot…' : <>Lock in my Founder badge <ArrowRight size={16} weight="bold" /></>}
            </button>
          </form>
          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <div className="text-[10px] uppercase tracking-widest text-white/35">or sign up with</div>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          <div className="mt-4 flex justify-center" data-testid="register-google-wrap">
            <GoogleSignInButton label="Sign in with Google" />
          </div>
          <div className="mt-6 text-sm text-white/55 text-center">
            Already inside?{' '}
            <Link data-testid="register-to-login" to="/login" className="text-bx-cyan font-bold">Sign in →</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
